import { Box, Card, Typography, Chip, Stack, RichTextReader, formatDate } from "@compass/core";
import { Button } from "@mui/material";
import { Theme } from "@mui/material/styles";
import BugReportIcon from "@mui/icons-material/BugReport";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { NotificationDto } from "generated/openApi";
import {
    APP_TOP_BAR_NOTIFICATIONS_COPY,
    NOTIFICATIONS_CARD_ACCENT_WIDTH_PX,
    NOTIFICATIONS_TIMELINE_DOT_BORDER_WIDTH_PX,
    NOTIFICATIONS_TIMELINE_GEOMETRY,
} from "./AppTopBarNotificationsConstants";

interface AppTopBarNotificationsCardProps {
    notification: NotificationDto;
    theme: Theme;
}

export const AppTopBarNotificationsCard = ({
    notification,
    theme,
}: AppTopBarNotificationsCardProps) => {
    const accentColor = notification.isRead
        ? theme.palette.text.secondary
        : theme.palette.mode === "dark"
            ? theme.palette.primary.light
            : theme.palette.primary.main;

    return (
        <Box sx={{ position: "relative", pb: 4 }}>
            {/* Timeline Dot */}
            <Box
                sx={{
                    position: "absolute",
                    left: NOTIFICATIONS_TIMELINE_GEOMETRY.dotLeftPx,
                    top: NOTIFICATIONS_TIMELINE_GEOMETRY.dotTopPx,
                    width: NOTIFICATIONS_TIMELINE_GEOMETRY.dotDiameterPx,
                    height: NOTIFICATIONS_TIMELINE_GEOMETRY.dotDiameterPx,
                    borderRadius: NOTIFICATIONS_TIMELINE_GEOMETRY.dotBorderRadius,
                    bgcolor: accentColor,
                    borderStyle: "solid",
                    borderWidth: NOTIFICATIONS_TIMELINE_DOT_BORDER_WIDTH_PX,
                    borderColor: theme.palette.background.paper,
                    zIndex: 1,
                }}
            />
            {/* Content offset from timeline */}
            <Box sx={{ pl: 6 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: theme.typography.fontWeightMedium,
                    }}
                >
                    {formatDate(notification.createdDate)}
                </Typography>
                {/* Notification Card */}
                <Card
                    sx={{
                        mt: 2,
                        borderLeftStyle: "solid",
                        borderLeftWidth: NOTIFICATIONS_CARD_ACCENT_WIDTH_PX,
                        borderLeftColor: accentColor,
                    }}
                >
                    {/* Title Row */}
                    <Stack direction="row" alignItems="center" spacing="sm" flexWrap="wrap">
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: notification.isRead
                                    ? theme.typography.fontWeightRegular
                                    : theme.typography.fontWeightBold,
                                flex: 1,
                            }}
                        >
                            {notification.title}
                        </Typography>
                        {notification.isBug && (
                            <Chip
                                icon={<BugReportIcon />}
                                label={APP_TOP_BAR_NOTIFICATIONS_COPY.bugFixLabel}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                    {/* Content */}
                    <RichTextReader html={notification.content} />
                    {/* User Manual Link */}
                    {notification.userManualLink && (
                        <Box sx={{ mt: 2 }}>
                            <Button
                                component="a"
                                href={notification.userManualLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<MenuBookIcon />}
                                size="small"
                                variant="text"
                            >
                                {APP_TOP_BAR_NOTIFICATIONS_COPY.userManualLabel}
                            </Button>
                        </Box>
                    )}
                </Card>
            </Box>
        </Box>
    );
};
